import type { AuditCategory, AuditContext, ClubAuditRow } from './types';
import { MODEL_LABEL } from './format';

/**
 * Beheerlogboek: audit_log-regels als gewone Nederlandse zinnen ("Sanne de Vries heeft deur 'Kleedkamer 1'
 * aangemaakt in Kantine"). Dekt de expliciete acties uit de migraties en Edge Functions (door.create,
 * club_admin.add, …), de generieke triggerregels (<tabel>.insert|update|delete, app.audit_row) en de demo-acties.
 * Onbekende acties krijgen een nette terugvaltekst.
 */

export const AUDIT_CATEGORY_LABEL: Record<AuditCategory, string> = {
  doors: 'Deuren & sloten',
  people: 'Personen & groepen',
  access: 'Toegang',
  admins: 'Beheerders',
  other: 'Overig',
};

const DOOR_ENTITIES = ['locations', 'buildings', 'doors', 'locks', 'lock_hardening'];
const DOOR_PREFIX = ['location', 'building', 'door', 'lock', 'locks', 'lock_hardening'];
const ACCESS_ENTITIES = ['access_rules', 'access_codes', 'credentials'];
const ACCESS_PREFIX = ['access_rules', 'access_code', 'credential'];
const ADMIN_ENTITIES = ['memberships', 'group_managers', 'installer_assignments', 'platform_staff'];
const ADMIN_PREFIX = ['club_admin', 'membership', 'impersonate', 'installer', 'staff'];
const PEOPLE_ENTITIES = ['persons', 'groups', 'group_members'];
const PEOPLE_PREFIX = ['person', 'persons', 'person_invite', 'group', 'group_member'];

/** Zelfde indeling als app.audit_category (migratie 0012). */
export function auditCategory(action: string, entity: string): AuditCategory {
  const prefix = action.split('.')[0] ?? '';
  if (DOOR_ENTITIES.includes(entity) || DOOR_PREFIX.includes(prefix)) return 'doors';
  if (ACCESS_ENTITIES.includes(entity) || ACCESS_PREFIX.includes(prefix)) return 'access';
  if (ADMIN_ENTITIES.includes(entity) || ADMIN_PREFIX.includes(prefix)) return 'admins';
  if (PEOPLE_ENTITIES.includes(entity) || PEOPLE_PREFIX.includes(prefix)) return 'people';
  return 'other';
}

const ROLE: Record<string, string> = {
  club_admin: 'clubbeheerder',
  group_manager: 'groepsbeheerder',
  member: 'lid',
  platform_admin: 'platformbeheerder',
  installer: 'installateur',
  support: 'support',
};
const STAFF_ROLES = ['platform_admin', 'support', 'installer'];
const CREDENTIAL: Record<string, string> = { app: 'app-toegang', card: 'pas', fingerprint: 'vingerafdruk', pin: 'pincode' };
const LOCK_STATUS: Record<string, string> = {
  in_stock: 'op voorraad',
  assigned: 'toegewezen',
  installed: 'geïnstalleerd',
  faulty: 'defect',
  retired: 'afgeschreven',
};
const FIELD: Record<string, string> = {
  name: 'naam',
  full_name: 'naam',
  kvk_number: 'KvK-nummer',
  log_retention_days: 'bewaartermijn logboek',
  revoke_alert_hours: 'melding openstaande intrekking',
  battery_alert_pct: 'batterijmelding',
  max_code_validity_days: 'maximale codeduur',
  status: 'status',
  email: 'e-mailadres',
  phone: 'telefoon',
  external_ref: 'lidnummer',
  is_minor: 'minderjarig',
  biometric_consent_at: 'toestemming vingerafdruk',
  valid_until: 'einddatum',
  valid_from: 'startdatum',
  season_label: 'seizoen',
  weekdays: 'dagen',
  start_minute: 'tijden',
  end_minute: 'tijden',
  all_day: 'tijden',
  kind: 'soort deur',
  escape_route: 'nooduitgang',
  passage_mode_allowed: 'passagemodus',
  address: 'adres',
  notes: 'notitie',
  door_thickness_mm: 'deurdikte',
};
const ENTITY: Record<string, string> = {
  organizations: 'de clubgegevens',
  locations: 'een locatie',
  buildings: 'een gebouw',
  doors: 'een deur',
  locks: 'een slot',
  persons: 'een persoon',
  groups: 'een groep',
  group_members: 'een groepslid',
  group_managers: 'een groepsbeheerder',
  access_rules: 'een toegangsregel',
  access_codes: 'een tijdelijke code',
  credentials: 'een pas of vingerafdruk',
  memberships: 'een beheerder',
  installer_assignments: 'een installateur',
};

type Json = Record<string, unknown> | null | undefined;
const s = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const q = (name: string | undefined) => (name ? `'${name}'` : '');

function changed(before: Json, after: Json): string[] {
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out: string[] = [];
  for (const k of keys) {
    if (k === 'updated_at' || k === 'created_at') continue;
    if (JSON.stringify(before[k] ?? null) !== JSON.stringify(after[k] ?? null)) out.push(k);
  }
  return out;
}

function fieldList(keys: string[]): string {
  const labels = [...new Set(keys.map((k) => FIELD[k]).filter(Boolean))];
  return labels.length ? ` (${labels.join(', ')})` : '';
}

export interface AuditTextOptions {
  /** Weergavenaam van een slotmodel; standaard MODEL_LABEL (centrale helper). */
  modelLabel?: (model: string) => string;
}

/** Wie het deed. Antreja-medewerkers krijgen "(Antreja)" achter hun naam; null = automatisch (systeem). */
export function auditActor(row: Pick<ClubAuditRow, 'actor_name' | 'actor_role'>): string | null {
  if (!row.actor_name) return null;
  return STAFF_ROLES.includes(row.actor_role) ? `${row.actor_name} (Antreja)` : row.actor_name;
}

/** De handeling zonder onderwerp: "deur 'Kleedkamer 1' aangemaakt in Kantine". */
export function auditPhrase(row: Pick<ClubAuditRow, 'action' | 'entity' | 'before' | 'after' | 'context'>, opts: AuditTextOptions = {}): string {
  const { action, entity } = row;
  const b = row.before ?? null;
  const a = row.after ?? null;
  const c: AuditContext = row.context ?? {};
  const model = (m: string) => (opts.modelLabel ? opts.modelLabel(m) : (MODEL_LABEL as Record<string, string>)[m] ?? m.replace('_', '-'));

  const name = s(a?.name) ?? s(b?.name);
  const door = () => (c.door || (entity === 'doors' && name) ? `deur ${q(c.door ?? name)}` : 'een deur');
  const where = () => {
    const bld = c.building;
    const loc = c.location;
    if (bld && loc) return ` in ${bld} (${loc})`;
    if (bld) return ` in ${bld}`;
    if (loc) return ` op ${loc}`;
    return '';
  };
  const lock = () => {
    const parts = [c.lock_model ? model(c.lock_model) : undefined, c.lock_serial].filter(Boolean).join(' ');
    return parts ? `slot ${parts}` : 'een slot';
  };
  const lockOnDoor = () => `${lock()}${c.door ? ` (deur ${q(c.door)})` : ''}`;
  const person = () => (c.person ? q(c.person) : s(a?.person_label) ? q(s(a?.person_label)) : 'een persoon');
  const group = () => (c.group ? `groep ${q(c.group)}` : 'een groep');
  const subject = () => (c.subject ? q(c.subject) : 'een beheerder');
  const role = (r: unknown) => ROLE[String(r)] ?? String(r ?? '');
  const cred = (t: unknown) => CREDENTIAL[String(t)] ?? 'toegangsmiddel';
  const code = (label: unknown) => (s(label) ? `tijdelijke code ${q(s(label))}` : 'een tijdelijke code');
  const keys = changed(b, a);
  const archived = !!a && !b?.archived_at && !!a.archived_at;

  switch (action) {
    // ---- deuren, gebouwen, locaties, sloten
    case 'door.create':
    case 'doors.insert':
      return `deur ${q(name ?? c.door)} aangemaakt${where()}`;
    case 'door.update':
    case 'doors.update': {
      const n = q(s(a?.name) ?? c.door);
      if (archived) return `deur ${n} gearchiveerd`;
      if (action === 'doors.update' && keys.includes('lock_id')) {
        return a?.lock_id ? `een slot gekoppeld aan deur ${n}` : `het slot ontkoppeld van deur ${n}`;
      }
      if (s(b?.name) && s(a?.name) && b?.name !== a?.name) return `deur ${q(s(b?.name))} hernoemd naar ${n}`;
      if (keys.includes('building_id')) return `deur ${n} verplaatst naar${where() || ' een ander gebouw'}`;
      return `deur ${n} gewijzigd${fieldList(keys)}`;
    }
    case 'door.archive':
      return `deur ${q(name ?? c.door)} gearchiveerd`;
    case 'doors.delete':
      return `deur ${q(name ?? c.door)} verwijderd`;
    case 'door.link_lock':
      return `${lock()} gekoppeld aan ${door()}`;
    case 'door.unlink_lock':
      return `${lock()} ontkoppeld van ${door()}`;
    case 'door.passage_mode':
      return `passagemodus ${a?.enabled ? 'aangezet' : 'uitgezet'} op ${door()}`;
    case 'door.auto_lock':
      return a?.enabled === false
        ? `automatisch op slot uitgezet op ${door()}`
        : `automatisch op slot na ${autoLockText(Number(a?.seconds ?? 5))} op ${door()}`;
    case 'loan_card.create':
      return `uitleenpas ${q(s(a?.label) ?? 'nieuw')} aangemaakt`;
    case 'loan_card.lend':
      return `${q(s(a?.card) ?? 'uitleenpas')} uitgeleend aan ${s(a?.guest_name) ?? 'een gast'}${s(a?.guest_club) ? ` (${s(a?.guest_club)})` : ''}`;
    case 'loan_card.return':
      return `${q(s(a?.card) ?? 'uitleenpas')} ingenomen van ${s(a?.guest_name) ?? 'een gast'}`;
    case 'installer.club_assign':
      return 'een installateur van de club aangewezen';
    case 'installer.club_end':
      return 'een installateur van de club beëindigd';
    case 'door.passage_on':
      return `passagemodus aangezet op ${door()}`;
    case 'door.passage_off':
      return `passagemodus uitgezet op ${door()}`;

    case 'location.create':
    case 'locations.insert':
      return `locatie ${q(name ?? c.location)} toegevoegd${s(a?.address) ? ` (${s(a?.address)})` : ''}`;
    case 'location.update':
    case 'locations.update': {
      const n = q(s(a?.name) ?? c.location);
      if (s(b?.name) && s(a?.name) && b?.name !== a?.name) return `locatie ${q(s(b?.name))} hernoemd naar ${n}`;
      if (keys.some((k) => ['address', 'street', 'house_number', 'house_number_addition', 'postal_code', 'city'].includes(k))) {
        return s(a?.address) ? `het adres van locatie ${n} gewijzigd naar ${s(a?.address)}` : `het adres van locatie ${n} gewijzigd`;
      }
      return `locatie ${n} gewijzigd`;
    }
    case 'location.delete':
    case 'locations.delete':
      return `locatie ${q(name ?? c.location)} verwijderd`;

    case 'building.create':
    case 'buildings.insert':
      return `gebouw ${q(name ?? c.building)} toegevoegd${c.location ? ` op ${c.location}` : ''}`;
    case 'building.update':
    case 'buildings.update': {
      const n = q(s(a?.name) ?? c.building);
      if (archived) return `gebouw ${n} gearchiveerd`;
      if (s(b?.name) && s(a?.name) && b?.name !== a?.name) return `gebouw ${q(s(b?.name))} hernoemd naar ${n}`;
      return `gebouw ${n} gewijzigd${fieldList(keys)}`;
    }
    case 'building.archive':
      return `gebouw ${q(name ?? c.building)} gearchiveerd`;
    case 'buildings.delete':
      return `gebouw ${q(name ?? c.building)} verwijderd`;

    case 'lock.battery_replaced':
      return `de batterijen vervangen van ${lockOnDoor()}`;
    case 'lock.assign':
      return `${lock()} aan de club toegewezen`;
    case 'locks.update': {
      if (keys.includes('organization_id')) return a?.organization_id ? `${lock()} aan de club toegewezen` : `${lock()} teruggezet naar de voorraad`;
      if (keys.includes('status')) {
        return a?.status === 'installed' ? `${lockOnDoor()} geïnstalleerd` : `de status van ${lock()} gewijzigd naar ${LOCK_STATUS[String(a?.status)] ?? String(a?.status)}`;
      }
      return `${lock()} gewijzigd`;
    }
    case 'lock_hardening.save':
      return a?.completed ? `de opleverchecklist afgerond voor ${lockOnDoor()}` : `de opleverchecklist opgeslagen voor ${lockOnDoor()}`;

    // ---- toegang: regels, codes, passen en vingers
    case 'access_rules.insert':
      return `${group()} toegang gegeven tot ${door()}`;
    case 'access_rules.update':
      return `de toegangstijden van ${group()} voor ${door()} gewijzigd`;
    case 'access_rules.delete':
      return `de toegang van ${group()} tot ${door()} ingetrokken`;
    case 'access_rules.save':
      return `de toegang van ${group()} aangepast`;
    case 'access_codes.insert':
    case 'access_code.create':
      return `${code(a?.label)} aangemaakt${c.door ? ` voor ${door()}` : ''}`;
    case 'access_codes.update':
      if (!b?.revoked_at && a?.revoked_at) return `${code(a?.label)} ingetrokken`;
      return `${code(a?.label)} gewijzigd`;
    case 'access_code.revoke':
      return `${code(a?.label)} ingetrokken${c.door ? ` voor ${door()}` : ''}`;
    case 'access_codes.delete':
      return `${code(b?.label)} verwijderd`;
    case 'credential.enroll':
    case 'credentials.insert':
      return `${cred(a?.type)} ingeleerd voor ${person()}${c.door ? ` op ${door()}` : ''}`;
    case 'credentials.update':
      if (['revoke_pending', 'revoked'].includes(String(a?.status)) && !['revoke_pending', 'revoked'].includes(String(b?.status))) {
        return `${cred(a?.type)} van ${person()} ingetrokken`;
      }
      if (a?.status === 'active' && b?.status === 'pending_enrollment') return `${cred(a?.type)} van ${person()} geactiveerd`;
      return `${cred(a?.type)} van ${person()} gewijzigd`;
    case 'credential.revoke':
      return `een pas of vingerafdruk van ${person()} ingetrokken`;
    case 'credentials.delete':
      return `${cred(b?.type)} van ${person()} verwijderd`;

    // ---- personen en groepen
    case 'persons.insert':
    case 'person.create':
      return `persoon ${q(s(a?.full_name) ?? c.person)} toegevoegd`;
    case 'persons.update': {
      const n = q(s(a?.full_name) ?? c.person);
      if (archived) return `persoon ${n} gearchiveerd`;
      if (keys.includes('profile_id')) return a?.profile_id ? `een app-account gekoppeld aan ${n}` : `de app-koppeling van ${n} verbroken`;
      return `de gegevens van ${n} gewijzigd${fieldList(keys)}`;
    }
    case 'person.archive':
      return c.person ? `persoon ${q(c.person)} gearchiveerd` : 'een persoon gearchiveerd';
    case 'persons.delete':
      return `persoon ${q(s(b?.full_name) ?? c.person)} verwijderd`;
    case 'persons.import':
      return 'personen geïmporteerd uit een bestand';
    case 'person_invite.create':
      return `${person()} uitgenodigd voor de app`;
    case 'person_invite.revoke':
      return `de app-uitnodiging van ${person()} ingetrokken`;
    case 'person_invite.claim':
    case 'person_invite.claimed':
    case 'person_invite.accepted':
      return 'de uitnodiging voor de app geaccepteerd';
    case 'person.app_unlink':
    case 'person.app_unlinked':
      return `de app-koppeling van ${person()} verbroken`;

    case 'groups.insert':
    case 'group.create':
      return `groep ${q(name ?? c.group)} aangemaakt`;
    case 'group.duplicate':
      return `groep ${q(name ?? c.group)} aangemaakt als kopie`;
    case 'groups.update': {
      const n = q(s(a?.name) ?? c.group);
      if (archived) return `groep ${n} gearchiveerd`;
      if (s(b?.name) && s(a?.name) && b?.name !== a?.name) return `groep ${q(s(b?.name))} hernoemd naar ${n}`;
      return `groep ${n} gewijzigd${fieldList(keys)}`;
    }
    case 'groups.delete':
      return `groep ${q(name ?? c.group)} verwijderd`;
    case 'group_members.insert':
    case 'group_member.add':
      return `${person()} toegevoegd aan ${group()}`;
    case 'group_members.delete':
    case 'group_member.remove':
      return `${person()} uit ${group()} gehaald`;
    case 'group_members.update':
      return `het lidmaatschap van ${person()} in ${group()} gewijzigd${fieldList(keys)}`;

    // ---- beheerders
    case 'club_admin.add':
      return `${subject()} toegevoegd als ${role(a?.role ?? 'club_admin')}`;
    case 'club_admin.invite_resent':
      return `de uitnodiging opnieuw gestuurd naar ${subject()}`;
    case 'membership.role':
    case 'memberships.update':
      return `de rol van ${subject()} gewijzigd naar ${role(a?.role)}`;
    case 'membership.remove_admin':
      return `de beheerrechten van ${subject()} ingetrokken`;
    case 'memberships.insert':
      return `${subject()} toegevoegd als ${role(a?.role)}`;
    case 'memberships.delete':
      return `${subject()} uit de club gehaald`;
    case 'group_managers.insert':
      return `${subject()} groepsbeheerder gemaakt van ${group()}`;
    case 'group_managers.delete':
      return `${subject()} weggehaald als groepsbeheerder van ${group()}`;
    case 'installer_assignments.insert':
    case 'installer.assign':
      return `installateur ${subject()} aan de club toegewezen`;
    case 'installer_assignments.update':
      return `de toewijzing van installateur ${subject()} verlengd`;
    case 'installer_assignments.delete':
      return `de toewijzing van installateur ${subject()} beëindigd`;
    case 'impersonate.start':
      return `meegekeken in het clubportaal${s(a?.reason) ? ` (reden: ${s(a?.reason)})` : ''}`;
    case 'impersonate.write_enabled':
      return `wijzigen ingeschakeld tijdens meekijken${s(a?.reason) ? ` (reden: ${s(a?.reason)})` : ''}`;
    case 'impersonate.stop':
      return 'het meekijken beëindigd';

    // ---- club
    case 'organization.create_with_admin':
    case 'organization.create':
    case 'organizations.insert':
      return 'de club aangemaakt';
    case 'organizations.update':
    case 'organization.update':
      return `de clubgegevens gewijzigd${fieldList(keys)}`;
  }

  // Terugval: generieke triggerregel of onbekende actie.
  const op = action.split('.').at(-1);
  const what = ENTITY[entity] ?? 'iets';
  if (op === 'insert') return `${what} toegevoegd`;
  if (op === 'delete') return `${what} verwijderd`;
  if (op === 'update') return `${what} gewijzigd`;
  return `een wijziging gedaan aan ${what}`;
}

/** Volledige zin: "Sanne de Vries heeft deur 'Kleedkamer 1' aangemaakt in Kantine". */
export function describeAudit(row: ClubAuditRow, opts: AuditTextOptions = {}): string {
  const phrase = auditPhrase(row, opts);
  const actor = auditActor(row);
  if (actor) return `${actor} heeft ${phrase}`;
  return `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)} (automatisch)`;
}

/** 5 → "5 seconden", 900 → "15 minuten", 90 → "1 minuut en 30 seconden". */
export function autoLockText(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const sec = (n: number) => `${n} ${n === 1 ? 'seconde' : 'seconden'}`;
  const min = (n: number) => `${n} ${n === 1 ? 'minuut' : 'minuten'}`;
  if (m === 0) return sec(s);
  return s ? `${min(m)} en ${sec(s)}` : min(m);
}
